import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { useProducts } from '../context/ProductContext';
import { useTheme } from '../components/ThemeProvider';
import Button from '../components/common/Button';
import { gsap } from 'gsap';
import { fadeIn } from '../utils/gsapAnimations'; // assuming you have this

function ProductDetail() {
  const { id } = useParams();
  const productId = parseInt(id, 10);
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const { products, loading, error: globalError } = useProducts();
  const { primaryColor } = useTheme();
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedView, setSelectedView] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState('');
  const [product, setProduct] = useState(null);
  const [fetchError, setFetchError] = useState('');

  const pageRef = useRef(null);

  useEffect(() => {
    fadeIn(pageRef.current, 0.4);
  }, []);

  useEffect(() => {
    if (!loading) {
      if (globalError) {
        setFetchError('Failed to load products');
      } else if (!products || products.length === 0) {
        setFetchError('No products available');
      } else {
        const foundProduct = products.find(p => p.id === productId);
        if (foundProduct) {
          setProduct(foundProduct);
          setSelectedView(foundProduct.imageViews ? Object.keys(foundProduct.imageViews)[0] : '');
        } else {
          setFetchError('Product not found');
        }
      }
    }
  }, [products, loading, globalError, productId]);

  const handleAddToCart = () => {
    if (!product) return;
    if ((product.category === 'clothing' || product.category === 'shoes') && (!selectedSize || !selectedColor)) {
      setError('Please select a size and color.');
      return;
    }
    if (product.stock < quantity && product.has_stock) {
      setError('Insufficient stock.');
      return;
    }
    setError('');
    const cartItem = {
      ...product,
      quantity,
      ...(product.category === 'clothing' || product.category === 'shoes' ? { selectedSize, selectedColor } : {})
    };
    addToCart(cartItem);

    // Visual feedback only
    gsap.to('.add-to-cart-btn', {
      scale: 1.06,
      duration: 0.18,
      yoyo: true,
      repeat: 1,
    });

    alert(`${product.name} added to cart!`);
  };

  const renderProperties = () => {
    if (!product) return null;

    switch (product.category) {
      case 'clothing':
      case 'shoes':
        return (
          <>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Material:</strong> {product.material || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Style:</strong> {product.style || 'N/A'}
            </p>
          </>
        );
      case 'books':
        return (
          <>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Author:</strong> {product.author || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">ISBN:</strong> {product.isbn || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Genre:</strong> {product.genre || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Publication Date:</strong> {product.publication_date || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Format:</strong> {product.format?.join(', ') || 'N/A'}
            </p>
          </>
        );
      case 'real_estate':
        return (
          <>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Location:</strong> {product.location || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Type:</strong> {product.type || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Bedrooms:</strong> {product.bedrooms || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Bathrooms:</strong> {product.bathrooms || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Square Footage:</strong> {product.squareFootage || 'N/A'} sq ft
            </p>
          </>
        );
      case 'cars':
        return (
          <>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Make:</strong> {product.make || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Model:</strong> {product.model || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Year:</strong> {product.year || 'N/A'}
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Mileage:</strong> {product.mileage || 'N/A'} miles
            </p>
            <p className="text-lg font-montserrat text-secondary leading-relaxed">
              <strong className="font-semibold">Fuel Type:</strong> {product.fuelType || 'N/A'}
            </p>
          </>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-primary text-xl font-montserrat animate-pulse">Loading...</div>;
  }

  if (fetchError || !product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <h1 className="text-4xl font-playfair-display font-semibold text-primary mb-6">Product Not Found</h1>
        <p className="text-xl font-montserrat text-secondary mb-10 max-w-2xl mx-auto">
          Sorry, we couldn’t find that product. Try browsing our collection.
        </p>
        <Link to="/shop">
          <Button className="bg-primary text-cream px-10 py-4 text-xl font-montserrat rounded-full hover:bg-primary/90 transition-all shadow-md hover:shadow-lg">
            Shop Now
          </Button>
        </Link>
      </div>
    );
  }

  const imageViews = product.imageViews ? Object.entries(product.imageViews) : [];

  return (
    <div ref={pageRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 lg:py-16 bg-cream">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Image Section */}
        <div className="space-y-6">
          <div className="rounded-2xl overflow-hidden shadow-2xl bg-white border border-gray-100/80">
            <img
              src={
                product.imageViews && selectedView
                  ? `${process.env.REACT_APP_PRODUCT_URL}${product.imageViews[selectedView]}`
                  : product.colorImages && selectedColor
                  ? `${process.env.REACT_APP_PRODUCT_URL}${product.colorImages[selectedColor]}`
                  : `${process.env.REACT_APP_PRODUCT_URL}${product.image}` || '/assets/images/placeholder.jpg'
              }
              alt={`${product.name} ${selectedView || selectedColor || ''}`}
              className="w-full aspect-[4/5] sm:aspect-square lg:aspect-[3/4] object-cover transition-transform duration-700 hover:scale-[1.03]"
              onError={() => console.error(`Failed to load image for ${product.name}`)}
            />
          </div>

          {/* Image Carousel – preserved exactly */}
          {imageViews.length > 0 && (
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {imageViews.map(([view, src]) => (
                <button
                  key={view}
                  onClick={() => setSelectedView(view)}
                  className={`flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden border-2 transition-all duration-300 shadow-sm hover:shadow-md ${
                    selectedView === view
                      ? 'border-primary scale-105'
                      : 'border-transparent hover:border-primary/60'
                  }`}
                  style={{ '--primary': primaryColor }}
                >
                  <img
                    src={`${process.env.REACT_APP_PRODUCT_URL}/${src}`}
                    alt={`${product.name} ${view}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Details */}
        <div className="space-y-8 lg:space-y-10">
          <div className="space-y-4">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-playfair-display font-semibold text-primary leading-tight">
              {product.name}
            </h1>

            <p className="text-3xl sm:text-4xl font-montserrat font-bold text-primary tracking-tight">
              {formatPrice ? formatPrice(product.price) : `$${product.price?.toFixed(2) || '—'}`}
            </p>

            <p className="text-lg sm:text-xl font-montserrat text-secondary leading-relaxed">
              {product.description || 'No description available.'}
            </p>

            <p className="text-lg font-montserrat text-secondary">
              <strong>Stock:</strong>{' '}
              {product.has_stock ? (
                product.stock > 0 ? (
                  <span className="text-green-600 font-semibold">In Stock</span>
                ) : (
                  <span className="text-red-600 font-semibold">Out of Stock</span>
                )
              ) : (
                '' // silent when !has_stock — as you preferred
              )}
            </p>
          </div>

          {/* Category-Specific Properties – preserved */}
          <div className="space-y-3 border-t border-gray-200 pt-6">
            {renderProperties()}
          </div>

          {/* Size Selection – preserved */}
          {(product.category === 'clothing' || product.category === 'shoes') && (
            <div className="space-y-3">
              <label className="text-lg font-montserrat font-semibold text-primary block">Size</label>
              <select
                value={selectedSize}
                onChange={e => setSelectedSize(e.target.value)}
                className="w-full max-w-xs p-3.5 border border-gray-300 rounded-xl text-base font-montserrat bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all shadow-sm hover:shadow"
                aria-label="Select size"
                style={{ '--primary': primaryColor }}
              >
                <option value="">Select a size</option>
                {product.sizes?.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Color Selection – preserved */}
          {(product.category === 'clothing' || product.category === 'shoes') && (
            <div className="space-y-3">
              <label className="text-lg font-montserrat font-semibold text-primary block">Color</label>
              <select
                value={selectedColor}
                onChange={e => setSelectedColor(e.target.value)}
                className="w-full max-w-xs p-3.5 border border-gray-300 rounded-xl text-base font-montserrat bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all shadow-sm hover:shadow"
                aria-label="Select color"
                style={{ '--primary': primaryColor }}
              >
                <option value="">Select a color</option>
                {product.colors?.map(color => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Quantity Selection – preserved */}
          {product.category !== 'real_estate' && product.category !== 'cars' && (
            <div className="space-y-3">
              <label className="text-lg font-montserrat font-semibold text-secondary block">Quantity</label>
              <input
                type="number"
                min="1"
                max={product.stock || 999}
                value={quantity}
                onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24 p-3.5 border border-gray-300 rounded-xl text-center text-lg font-montserrat bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all shadow-sm hover:shadow"
                style={{ '--primary': primaryColor }}
              />
            </div>
          )}

          {/* Error Message */}
          {error && (
            <p className="text-base font-montserrat text-red-600 bg-red-50 p-4 rounded-xl border border-red-200">
              {error}
            </p>
          )}

          {/* Action Buttons – preserved */}
          {product.category === 'real_estate' || product.category === 'cars' ? (
            <div className="flex flex-col sm:flex-row gap-5 pt-4">
              <a
                href={`mailto:${product.contactInfo?.email}`}
                className="bg-primary text-cream px-10 py-4 text-lg font-montserrat rounded-full hover:bg-primary/90 transition-all shadow-md hover:shadow-lg text-center"
                style={{ '--primary': primaryColor }}
              >
                Email Us
              </a>
              <a
                href={`tel:${product.contactInfo?.phone}`}
                className="bg-primary text-cream px-10 py-4 text-lg font-montserrat rounded-full hover:bg-primary/90 transition-all shadow-md hover:shadow-lg text-center"
                style={{ '--primary': primaryColor }}
              >
                Call Us
              </a>
            </div>
          ) : (
            <Button
              onClick={handleAddToCart}
              className="add-to-cart-btn w-full sm:w-auto bg-primary text-cream px-12 py-5 text-xl font-montserrat rounded-full hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={product.stock === 0}
              style={{ '--primary': primaryColor }}
            >
              Add to Cart
            </Button>
          )}

          <Link
            to="/shop"
            className="block text-lg font-montserrat text-primary hover:text-primary/80 hover:underline transition-colors mt-6"
          >
            ← Back to Shop
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;